import React, { useEffect, useRef, useState } from "react";
import { Container, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./Register.css";
import illustration from "../../../assets/ilustracao.png";
import logo from "../../../assets/Logo.png";
import { register } from "../../../api/auth/register";
import formatPhone from "../../../utils/formatPhone";
import { RegistrationValues, RegistrationErrors, validateRegistration, registrationFailure } from "./registerValidation";

interface RegisterProps { onNavigate?: () => void; }
const fields: { name: keyof RegistrationValues; label: string; type: string; placeholder: string; autoComplete: string }[] = [
  { name: "email", label: "E-mail", type: "email", placeholder: "Insira o e-mail", autoComplete: "email" },
  { name: "name", label: "Nome", type: "text", placeholder: "Seu nome", autoComplete: "name" },
  { name: "phone", label: "Telefone", type: "tel", placeholder: "Número de telefone", autoComplete: "tel" },
  { name: "password", label: "Senha", type: "password", placeholder: "Crie uma senha", autoComplete: "new-password" },
  { name: "confirmPassword", label: "Confirmar senha", type: "password", placeholder: "Repita a senha", autoComplete: "new-password" },
];

export default function RegisterPage({ onNavigate }: RegisterProps) {
  const navigate = useNavigate();
  const [values, setValues] = useState<RegistrationValues>({ email: "", name: "", phone: "", password: "", confirmPassword: "" });
  const [errors, setErrors] = useState<RegistrationErrors>({});
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const feedbackRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => { if (message) feedbackRef.current?.focus(); }, [message]);
  const focusField = (validation: RegistrationErrors) => {
    const first = fields.find(field => validation[field.name]);
    if (first) formRef.current?.querySelector<HTMLInputElement>(`[name="${first.name}"]`)?.focus();
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || success) return;
    setMessage("");
    const validation = validateRegistration(values);
    setErrors(validation);
    if (Object.keys(validation).length) { focusField(validation); return; }
    submitting.current = true; setLoading(true);
    try {
      await register({ nome_completo: values.name.trim(), email: values.email.trim(), senha: values.password, telefone: values.phone.replace(/\D/g, "") });
      setSuccess(true); setMessage("Cadastro realizado. Abra seu e-mail e clique no link de ativação. Confira também a caixa de spam.");
      setValues(previous => ({ ...previous, password: "", confirmPassword: "" }));
      timer.current = setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      const failure = registrationFailure(error);
      setErrors(failure.fields); setMessage(failure.message);
    } finally { submitting.current = false; setLoading(false); }
  };
  return <Container fluid className="login-container registration-page">
    <div className="left-section">
      <div className="branding"><img src={logo} alt="Insecta" className="logo" width={55} height={55} onClick={() => navigate("/home")} /><span className="brand-name">Insecta</span></div>
      <div className="login-content"><h1 className="title">Cadastrar</h1><h2 className="title">Preencha suas<br />informações para se<br />juntar a nós.</h2>
        <p className="title">Se você já tem uma conta, você pode<br />fazer <a href="/login" className="register-link" onClick={onNavigate}>login aqui!</a></p>
      </div>
      <div className="illustration-container"><img src={illustration} alt="Ilustração" className="illustration" /></div>
    </div>
    <div className="right-section"><div className="form-container">
      <p className="form-title">Insira suas informações abaixo</p>
      <Form ref={formRef} onSubmit={handleSubmit} className="login-form" noValidate aria-label="Criar conta" aria-busy={loading}>
        {message && <div ref={feedbackRef} tabIndex={-1} role={success ? "status" : "alert"} className={`registration-feedback${success ? " is-success" : ""}`}>{message}</div>}
        {fields.map(field => <Form.Group className="mb-3" controlId={`register-${field.name}`} key={field.name}>
          <Form.Label>{field.label}</Form.Label>
          <Form.Control name={field.name} type={field.type} placeholder={field.placeholder} value={values[field.name]}
            required autoComplete={field.autoComplete} disabled={loading || success} className="form-input"
            minLength={field.name === "password" ? 8 : undefined} maxLength={field.name === "name" ? 255 : undefined}
            aria-invalid={!!errors[field.name]}
            aria-describedby={errors[field.name] ? `register-${field.name}-error` : undefined}
            onChange={event => {
              const value = field.name === "phone" ? formatPhone(event.target.value) : event.target.value;
              setValues(previous => ({ ...previous, [field.name]: value }));
              setErrors(previous => ({ ...previous, [field.name]: undefined, ...(field.name === "password" ? { confirmPassword: undefined } : {}) }));
            }} />
          {errors[field.name] && <div id={`register-${field.name}-error`} className="registration-field-error" role="alert">{errors[field.name]}</div>}
        </Form.Group>)}
        <Button type="submit" className="login-button" disabled={loading || success}>{loading ? "Registrando…" : "Registrar"}</Button>
      </Form>
    </div></div>
  </Container>;
}
